import axios, { type AxiosError } from 'axios'

import { useAppConfigStore } from './shared/store/app-config.store'
import { useSessionStore } from './shared/store/session.store'

let isHandling401 = false

axios.interceptors.response.use(
  async function (response) {
    const fe_version = (response.headers as Record<string, string | undefined>)[
      'fe-version'
    ]
    if (fe_version != null) {
      const storedVersion = localStorage.getItem('fe-version')
      if (fe_version !== storedVersion && response.config.method === 'get') {
        localStorage.setItem('fe-version', fe_version)
        if (storedVersion != null) {
          window.location.reload()
        }
      }
    }
    return Promise.resolve(response)
  },
  async function (error: AxiosError) {
    if (error.response?.status === 401 && !isHandling401) {
      isHandling401 = true
      const wasConnected = useSessionStore.getState().key != null

      useSessionStore.getState().resetLoginOn401()

      if (wasConnected) {
        // Session expired mid-session: redirect to login and reload to clear
        // stale state from the previous authenticated session. The redirect
        // target is persisted in sessionStorage so it survives the reload.
        const { default: router } = await import('./router')
        const currentRoute = router.currentRoute.value.fullPath || ''
        useSessionStore.getState().setRedirectAfterLogin(currentRoute)
        await router.replace('/login').catch(() => {
          /* already on login */
        })
        window.location.reload()
      }
      // When wasConnected=false, the user was never authenticated (or the
      // session was already cleared by a prior 401). The router beforeEach
      // guard will redirect to /login. No reload needed — that would loop.
      // Delay reset to cover concurrent 401 responses from parallel requests.
      setTimeout(() => {
        isHandling401 = false
      }, 2000)
    }
    return Promise.reject(error)
  },
)

axios.interceptors.request.use(async function (config) {
  const isMutationMethod = ['post', 'put', 'patch', 'delete'].includes(
    config.method ?? '',
  )
  const isExcludedPath = [
    '/api/applicationConfig/markDirty',
    '/api/analytics',
    '/api/users',
    '/api/sessions',
    '/api/logout',
    '/api/notif',
  ].some((path) => config.url?.startsWith(path) ?? false)

  if (
    isMutationMethod &&
    !isExcludedPath &&
    !(useAppConfigStore.getState().appConfig?.isDirty ?? false)
  ) {
    await useAppConfigStore.getState().markDirty()
  }
  return config
})
